import { useState } from 'react';
import { apiError } from '@/lib/api-error';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSignature, Loader2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import PackageCard from '@/components/offers/PackageCard';
import { OFFER_STATUS_TONE } from '@/components/offers/statusTone';
import { useChat } from '@/context/ChatProvider';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { money, type Offer } from '@/types/offer';
import { ServerContractsSection, useServerContracts } from '@/components/contracts/ServerContracts';

export default function ContractsPage() {
  const queryClient = useQueryClient();
  const { openChat } = useChat();
  const [chosen, setChosen] = useState<Record<string, string>>({});

  const { data: offers, isLoading, error } = useQuery<Offer[]>({
    queryKey: ['contracts'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/contracts/');
      return res.data?.results ?? res.data ?? [];
    },
    retry: false,
  });

  const { data: serverContracts } = useServerContracts();

  const answer = useMutation({
    mutationFn: async (
      { id, what, packageId }: { id: string; what: 'accept' | 'decline' | 'cancel'; packageId?: string },
    ) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post(`/contracts/${id}/${what}/`, packageId ? { package: packageId } : {});
    },
    onSuccess: (_data, { what }) => {
      toast.success(
        what === 'accept' ? 'Accepted. This is now your contract.'
          : what === 'cancel' ? 'The contract has been cancelled.'
          : 'Declined.',
      );
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
    },
    onError: (error) => toast.error(apiError(error, 'That did not work.')),
  });

  if (error) {
    return (
      <p
        className="py-16 text-center text-sm text-muted-foreground"
        data-testid="contracts-forbidden"
      >
        {apiError(error, 'You cannot see this organisation’s contracts.')}
      </p>
    );
  }

  const waiting = (offers ?? []).filter((offer) => offer.status === 'sent');
  const requested = (offers ?? []).filter((offer) => offer.status === 'requested');
  const settled = (offers ?? []).filter((offer) => offer.status !== 'sent' && offer.status !== 'requested');

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Contracts</h2>
        <p className="text-muted-foreground">
          The servers you run, what your advisor has offered you, and what you have agreed to pay.
        </p>
      </div>

      <ServerContractsSection />

      {(offers ?? []).length > 0 && (serverContracts?.servers ?? []).length > 0 && (
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Advisor contracts</h3>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (offers ?? []).length === 0 ? (
        <div
          className="rounded-lg border border-dashed px-6 py-16 text-center"
          data-testid="contracts-empty"
        >
          <FileSignature className="mx-auto h-6 w-6 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            You don't have any support contracts yet.
          </p>
          <Button className="mt-4" variant="outline" onClick={() => openChat()} data-testid="contracts-empty-chat">
            <MessageSquare className="mr-2 h-4 w-4" />
            Contact your automation advisor
          </Button>
        </div>
      ) : (
        <>
          {requested.map((offer) => (
            <Card key={offer.id} data-testid={`contract-${offer.id}`}>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">{offer.title}</CardTitle>
                    <p className="mt-1 text-sm text-muted-foreground">
                      You asked for this from the advisor's public profile. Nothing is agreed until they confirm it; you will see their answer here and in your conversation.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={OFFER_STATUS_TONE.requested} data-testid={`contract-status-${offer.id}`}>Waiting for the advisor</Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => answer.mutate({ id: offer.id, what: 'decline' })}
                      disabled={answer.isPending}
                      data-testid={`withdraw-request-${offer.id}`}
                    >
                      Withdraw request
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {offer.packages
                    .filter((pkg) => offer.accepted_package === pkg.id)
                    .map((pkg) => (
                      <PackageCard key={pkg.id} pkg={pkg} currency={offer.currency} chosen testId={`contract-package-${pkg.id}`} />
                    ))}
                </div>
              </CardContent>
            </Card>
          ))}

          {waiting.map((offer) => (
            <Card key={offer.id} data-testid={`contract-${offer.id}`}>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">{offer.title}</CardTitle>
                    {offer.introduction && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        {offer.introduction}
                      </p>
                    )}
                  </div>
                  <Badge className={OFFER_STATUS_TONE[offer.status]}>Waiting for you</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {offer.packages.length > 1
                    ? 'Choose the package you want, then accept.'
                    : 'Accept to make this your contract.'}
                </p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {offer.packages.map((pkg) => (
                    <PackageCard
                      key={pkg.id}
                      pkg={pkg}
                      currency={offer.currency}
                      chosen={
                        (chosen[offer.id] ?? (offer.packages.length === 1 ? pkg.id : '')) === pkg.id
                      }
                      onChoose={() => setChosen((c) => ({ ...c, [offer.id]: pkg.id! }))}
                      testId={`choose-package-${pkg.id}`}
                    />
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => answer.mutate({
                      id: offer.id,
                      what: 'accept',
                      packageId:
                        chosen[offer.id]
                        ?? (offer.packages.length === 1 ? offer.packages[0].id : undefined),
                    })}
                    disabled={
                      answer.isPending
                      || (offer.packages.length > 1 && !chosen[offer.id])
                    }
                    data-testid={`accept-offer-${offer.id}`}
                  >
                    {answer.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Accept
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => answer.mutate({ id: offer.id, what: 'decline' })}
                    disabled={answer.isPending}
                    data-testid={`decline-offer-${offer.id}`}
                  >
                    Decline
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          {settled.map((offer) => (
            <Card key={offer.id} data-testid={`contract-${offer.id}`}>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">{offer.title}</CardTitle>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {offer.is_running && offer.monthly_fee
                        ? `${money(offer.monthly_fee, offer.currency)} a month, excl. VAT`
                        : offer.ended_at ? 'This contract has ended' : offer.currency}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      className={OFFER_STATUS_TONE[offer.status]}
                      data-testid={`contract-status-${offer.id}`}
                    >
                      {offer.ended_at ? 'Ended' : offer.status === 'rejected' ? 'Advisor could not take it on' : offer.status_display}
                    </Badge>
                    {offer.is_running && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" data-testid={`cancel-contract-${offer.id}`}>
                            Cancel contract
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid="cancel-contract-dialog">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Cancel this contract?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {offer.title} ends today. Anything already billed stays billed, and hours your advisor has logged this month are still charged. Your advisor is told in your conversation.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Keep it</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => answer.mutate({ id: offer.id, what: 'cancel' })}
                              data-testid={`confirm-cancel-contract-${offer.id}`}
                            >
                              Cancel the contract
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {offer.status === 'rejected' && offer.rejection_reason && (
                  <p className="text-sm text-muted-foreground" data-testid={`rejection-reason-${offer.id}`}>
                    They said: {offer.rejection_reason}
                  </p>
                )}
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {offer.packages
                    .filter((pkg) => offer.status !== 'accepted' || offer.accepted_package === pkg.id)
                    .map((pkg) => (
                      <PackageCard
                        key={pkg.id}
                        pkg={pkg}
                        currency={offer.currency}
                        chosen={offer.accepted_package === pkg.id}
                        testId={`contract-package-${pkg.id}`}
                      />
                    ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}
